import { usePathname } from 'next/navigation';
import { useStores } from '@/utils/hooks/useStores';
import React, { useEffect, useState } from 'react';
import { IBreadcrumb } from 'tkmp-lib/src/interfaces';
import { useRouter } from 'next/router';
import {
    EHelpCategories,
    EHelpLegalInformationPages,
    EHelpSuppliersPages,
} from 'tkmp-lib/src/enums';
import Head from 'next/head';
import logo from '/public/static/images/image_logo_full.svg';
import { Flex } from 'antd';
import hp from '@/pages/help/HelpPage.module.scss';
import { Menu } from 'antd';
import useLocalization from 'tkmp-lib/src/hooks/useLocalization';

const definePaths = (pathname: string) => {
    if (pathname === '/help') {
        return {
            category: EHelpCategories.Customers,
            page: '',
        };
    } else if (pathname.split('/').slice(1).length > 1) {
        return {
            category: pathname.split('/').slice(1)[1],
            page: pathname.split('/').slice(1)[2],
        };
    } else if (pathname.split('/').slice(1).length === 1) {
        return {
            category: pathname.split('/').slice(1)[0],
            page: '',
        };
    }
    return {
        category: EHelpCategories.Customers,
        page: '',
    };
};

const HelpContainer = ({ children }: { children: React.ReactNode }) => {
    const LOCALIZATION = useLocalization();
    const helpMenuItems = [
        {
            label: LOCALIZATION.routes.forBuyers,
            key: EHelpCategories.Customers,
        },
        {
            label: LOCALIZATION.routes.forSuppliers,
            key: EHelpCategories.Suppliers,
            children: [
                {
                    label: LOCALIZATION.routes.manInstruction,
                    key: EHelpSuppliersPages.InstructionsForManufacturers,
                },
                {
                    label: LOCALIZATION.routes.companyInstruction,
                    key: EHelpSuppliersPages.InstructionsForTradingCompany,
                },
            ],
        },
        {
            label: LOCALIZATION.routes.about,
            key: EHelpCategories.AboutProject,
        },
        {
            label: LOCALIZATION.routes.TDMIstok,
            key: EHelpCategories.TDM,
        },
        {
            label: LOCALIZATION.routes.legalInfo,
            key: EHelpCategories.LegalInformation,
            children: [
                {
                    label: LOCALIZATION.routes.generalProvisions,
                    key: EHelpLegalInformationPages.GeneralProvisions,
                },
                {
                    label: LOCALIZATION.routes.userAgree,
                    key: EHelpLegalInformationPages.UserAgreement,
                },
                {
                    label: LOCALIZATION.routes.PDPP,
                    key: EHelpLegalInformationPages.PersonalDataProcessingPolicy,
                },
            ],
            disabled: false,
        },
    ];
    const pathname = usePathname();
    const router = useRouter();

    const [currentCategory, setCurrentCategory] = useState(
        (definePaths(pathname).category as EHelpCategories) ?? EHelpCategories.Customers,
    );
    const [currentCategoryPage, setCurrentCategoryPage] = useState<any>(definePaths(pathname).page);

    const {
        systemStore: { BREADCRUMBS, setBreadcrumbs },
    } = useStores();

    useEffect(() => {
        if (BREADCRUMBS.length === 0 || pathname === '/help') {
            const breads: IBreadcrumb[] = [
                {
                    key: '/',
                    title: LOCALIZATION.routes.home,
                },
                {
                    key: '/help',
                    title: LOCALIZATION.routes.help,
                },
            ];
            setBreadcrumbs(breads);
        }
    }, []);

    useEffect(() => {
        setCurrentCategory(
            (definePaths(pathname).category as EHelpCategories) ?? EHelpCategories.Customers,
        );
        setCurrentCategoryPage(definePaths(pathname).page);
    }, [router, LOCALIZATION.locale]);

    useEffect(() => {
        if (currentCategory) {
            const isExistCategory = helpMenuItems
                .map((item) => item.key)
                .includes(currentCategory as EHelpCategories);
            // @ts-ignore
            const isExistPage =
                !currentCategoryPage ||
                (isExistCategory &&
                    helpMenuItems
                        .filter((f) => f.children)
                        .map((item) => item.children)
                        .flat()
                        .map((item: any) => item.key)
                        .includes(currentCategoryPage));

            if (!(isExistCategory && isExistPage)) {
                router.push({
                    pathname: '/help',
                });
            }

            const currentMenuCategory: any = helpMenuItems.find(
                (f: any) => f.key === currentCategory,
            );
            let currentMenuPage;

            if (currentMenuCategory) {
                if (currentMenuCategory.children) {
                    currentMenuPage = currentMenuCategory.children.find(
                        (f: any) => f.key === currentCategoryPage,
                    );
                    if (currentMenuPage) {
                        const breads: IBreadcrumb[] = [
                            {
                                key: '/',
                                title: LOCALIZATION.routes.home,
                            },
                            {
                                key: '/help',
                                title: LOCALIZATION.routes.help,
                            },
                            {
                                key: `/help/${currentMenuCategory.key}`,
                                title: currentMenuCategory.label,
                                disabled: true,
                            },
                            {
                                key: `/help/${currentMenuCategory.key}/${currentMenuPage.key}`,
                                title: currentMenuPage.label,
                            },
                        ];
                        setBreadcrumbs(breads);
                    }
                } else {
                    const breads: IBreadcrumb[] = [
                        {
                            key: '/',
                            title: LOCALIZATION.routes.home,
                        },
                        {
                            key: '/help',
                            title: LOCALIZATION.routes.help,
                        },
                        {
                            key: `/help/${currentMenuCategory.key}`,
                            title: currentMenuCategory.label,
                        },
                    ];
                    setBreadcrumbs(breads);
                }
            }
        }
    }, [currentCategory, currentCategoryPage, LOCALIZATION]);

    return (
        <>
            {process.env.NEXT_PUBLIC_INCLUDE_SEO_TAGS === 'true' && (
                <Head>
                    <title>{'Помощь в TKMP.Istok'}</title>
                    <meta
                        name="description"
                        content="TKMP.Istok - ваш надежный B2B маркетплейс для поиска поставщиков и закупок товаров."
                    />
                    <meta
                        name="keywords"
                        content="B2B, маркетплейс, закупки, поставщики, TKMP.Istok, производители"
                    />
                    <meta
                        property="og:title"
                        content="Помощь в TKMP.Istok"
                    />
                    <meta
                        property="og:description"
                        content="TKMP.Istok - ваш надежный B2B маркетплейс для поиска поставщиков и закупок товаров."
                    />
                    <meta
                        property="og:url"
                        content={process.env.NEXT_PUBLIC_DOMAIN}
                    />
                    <meta
                        property="og:image"
                        content={logo.src}
                    />
                    <meta
                        property="og:type"
                        content="website"
                    />
                    <meta
                        name="twitter:card"
                        content="summary_large_image"
                    />
                    <meta
                        name="twitter:title"
                        content="Помощь в TKMP.Istok"
                    />
                    <meta
                        name="twitter:description"
                        content="TKMP.Istok - ваш надежный B2B маркетплейс для поиска поставщиков и закупок товаров."
                    />
                    <meta
                        name="twitter:image"
                        content={logo.src}
                    />
                </Head>
            )}
            <Flex
                vertical
                className={`${hp.container}`}
            >
                <Menu
                    className={hp.Menu}
                    onClick={(category) => {
                        if (category.keyPath.length === 1) {
                            router.push({
                                pathname: `/help/${category.key}`,
                            });
                            setCurrentCategory(category.key as EHelpCategories);
                        } else {
                            router.push({
                                pathname: `/help/${category.keyPath[1]}/${category.keyPath[0]}`,
                            });
                            setCurrentCategory(category.keyPath[1] as EHelpCategories);
                            setCurrentCategoryPage(category.keyPath[0]);
                        }
                    }}
                    selectedKeys={[currentCategory]}
                    mode={'horizontal'}
                    items={helpMenuItems}
                />
                <Flex className={hp.resultContainer}>{children}</Flex>
            </Flex>
        </>
    );
};

export default HelpContainer;
